import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, AlertTriangle, Info, Loader, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: <CheckCircle size={16} />,
  error:   <XCircle size={16} />,
  warning: <AlertTriangle size={16} />,
  info:    <Info size={16} />,
  loading: <Loader size={16} />,
};

// error type maps to the 'danger' CSS class
const ALERT_CLASS = {
  success: 'alert-success',
  error:   'alert-danger',
  warning: 'alert-warning',
  info:    'alert-info',
  loading: 'alert-loading',
};

const DEFAULT_DURATIONS = {
  success: 4000,
  error:   6000,
  warning: 6000,
  info:    4000,
  loading: 0,
};

const MAX_TOASTS = 4;

function ToastList({ toasts, dismiss }) {
  const { t } = useTranslation();
  return (
    <div
      className="toast-container"
      role="region"
      aria-label={t('a11y.notifications')}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((item) => (
        <div
          key={item.id}
          role="alert"
          className={`alert ${ALERT_CLASS[item.type] ?? 'alert-info'} toast-item`}
        >
          <span
            className={item.type === 'loading' ? 'toast-icon toast-icon-loading' : 'toast-icon'}
            aria-hidden="true"
          >
            {ICONS[item.type]}
          </span>
          <span className="toast-message">{item.message}</span>
          {item.action && (
            <button
              className="toast-action-btn"
              onClick={() => { item.action.onClick(); dismiss(item.id); }}
            >
              {item.action.label}
            </button>
          )}
          <button
            className="toast-dismiss-btn"
            onClick={() => dismiss(item.id)}
            aria-label={t('a11y.dismissNotification')}
          >
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', options = {}) => {
    // Back-compat: options can be a bare number (duration in ms)
    const opts = typeof options === 'number' ? { duration: options } : (options ?? {});
    const { duration, action } = opts;

    const id = `${Date.now()}-${Math.random()}`;

    setToasts((prev) => {
      // Don't stack identical toasts
      if (prev.some((t) => t.message === message && t.type === type)) return prev;
      // Cap at MAX_TOASTS — drop the oldest
      const next = [...prev, { id, message, type, action }];
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
    });

    const ms = duration ?? DEFAULT_DURATIONS[type] ?? 4000;
    if (ms > 0) {
      timersRef.current[id] = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        delete timersRef.current[id];
      }, ms);
    }

    return id;
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastList toasts={toasts} dismiss={dismiss} />
      <style>{`
        .toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 2000;
          pointer-events: none;
        }
        .toast-container > * { pointer-events: auto; }

        .toast-item {
          min-width: 288px;
          max-width: 400px;
          box-shadow: var(--shadow-lg);
          animation: toast-slide-in 0.18s ease;
          align-items: center;
          border-left-width: 3px;
          padding: 10px 12px 10px 14px;
        }

        .toast-icon {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .toast-icon-loading svg {
          animation: toast-spin 1s linear infinite;
        }

        .toast-message {
          flex: 1;
          font-size: var(--font-size-sm);
          line-height: 1.45;
        }

        .toast-action-btn {
          margin-left: 8px;
          padding: 2px 10px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
          border: 1px solid currentColor;
          border-radius: var(--radius-sm);
          opacity: 0.75;
          cursor: pointer;
          background: transparent;
          color: inherit;
          line-height: 1.6;
          flex-shrink: 0;
          transition: opacity var(--transition-fast);
        }
        .toast-action-btn:hover { opacity: 1; }

        .toast-dismiss-btn {
          margin-left: 4px;
          padding: 4px;
          flex-shrink: 0;
          background: transparent;
          border: none;
          cursor: pointer;
          color: currentColor;
          opacity: 0.5;
          border-radius: var(--radius-xs);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity var(--transition-fast);
        }
        .toast-dismiss-btn:hover { opacity: 1; }
        .toast-dismiss-btn:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
          opacity: 1;
        }

        /* Loading toast — neutral surface style */
        .alert-loading {
          background: var(--surface-1);
          border-color: var(--border-color);
          color: var(--text-primary);
        }
        [data-theme='dark'] .alert-loading {
          background: var(--surface-2);
          border-color: var(--border-color-strong);
          color: var(--text-primary);
        }

        @keyframes toast-slide-in {
          from { opacity: 0; transform: translateX(12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes toast-spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 640px) {
          .toast-container {
            /* Clear the bottom nav + the safe-area inset on notched phones */
            bottom: calc(84px + env(safe-area-inset-bottom));
            right: 12px;
            left: 12px;
          }
          .toast-item {
            min-width: 0;
            max-width: none;
            width: 100%;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
